import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { OrderDetailService } from '../../../core/services/api/orderdetail.service';
import { OrderDetailDto, OrderDetailStudy } from '../../../core/models/dtos/orderDetailDto';
import { MessageService } from 'primeng/api';
import { OrderService } from '../../../core/services/api/order.service';
import { NewOrderService } from '../../../core/services/api/neworder.service';
import { AuthService } from '../../../../../core/services/api/auth.service';

@Component({
    selector: 'app-order-detail',
    standalone: true,
    imports: [CommonModule, TableModule, TagModule, ButtonModule, ProgressBarModule, CardModule, DividerModule,
        FormsModule, ReactiveFormsModule, DialogModule, InputTextModule, SelectModule, MessageModule, TooltipModule],
    templateUrl: './order-detail.component.html',
    styleUrl: './order-detail.component.scss'
})
export class OrderDetailComponent implements OnInit {
    private readonly config = inject(DynamicDialogConfig);
    private readonly ref = inject(DynamicDialogRef);
    private readonly orderDetailService = inject(OrderDetailService);
    private readonly messageService = inject(MessageService);
    private readonly orderService = inject(OrderService);
    private readonly newOrderService = inject(NewOrderService);
    private readonly fb = inject(FormBuilder);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly authService = inject(AuthService);

    orderDetail: OrderDetailDto | null = null;
    studies: OrderDetailStudy[] = [];
    loading: boolean = true;
    idOrder: number = 0;
    rolUser: string[] = [];

    statusOrderList: Array<{ id: number; code: string }> = [];
    statusOrderMap: { [id: number]: string } = {};

    statusStudyMap: { [id: number]: string } = {};
    statusStudyList: Array<{ id: number; code: string }> = [];

    // Doctor change
    showChangeDoctorModal: boolean = false;
    doctorSearchForm!: FormGroup;
    doctorForm!: FormGroup;
    showDoctorCreateModal: boolean = false;
    searchingDoctor: boolean = false;
    searchPerformedDoctor: boolean = false;
    doctorsResults: any[] = [];
    selectedNewDoctor: any = null;
    savingDoctor: boolean = false;
    updatingDoctor: boolean = false;
    specialty: any[] = [];
    gender: any[] = [];
    createAsBaseBranchDoctor: boolean = false;

    // Insurance change
    showChangeInsuranceModal: boolean = false;
    secures: any[] = [];
    selectedNewSecure: any = null;
    subsidiaries: any[] = [];
    selectedNewSubsidiary: any = null;
    loadingSecures: boolean = false;
    loadingSubsidiaries: boolean = false;
    updatingInsurance: boolean = false;

    ngOnInit(): void {
        this.idOrder = this.config.data?.idOrder;
        this.rolUser = this.authService.getUserRoles();
        this.initForms();
        this.loadTypes();
        this.loadSecures();

        this.orderService.getstatuscab().subscribe({
            next: (result) => {
                if (result && Array.isArray(result.data)) {
                    this.statusOrderMap = {};
                    this.statusOrderList = result.data.map((item: any) => {
                        this.statusOrderMap[item.idStatus] = item.code;
                        return { id: item.idStatus, code: item.code };
                    });
                }
            },
            error: (err) => {
                console.error('Error al cargar status ORDER', err);
            }
        });

        this.orderService.getstatus().subscribe({
            next: (result) => {
                if (result && Array.isArray(result.data)) {
                    this.statusStudyMap = {};
                    this.statusStudyList = result.data.map((item: any) => {
                        this.statusStudyMap[item.idStatus] = item.code;
                        return { id: item.idStatus, code: item.code };
                    });
                }
            },
            error: (err) => console.error('Error al cargar estados de estudio', err)
        });

        if (this.idOrder) {
            this.loadOrderDetail();
        } else {
            this.showError('No se proporcionó ID de orden');
            this.loading = false;
        }
    }

    private initForms(): void {
        this.doctorSearchForm = this.fb.group({
            namedoctor: ['', [Validators.required, Validators.minLength(3)]]
        });

        this.doctorForm = this.fb.group({
            name: ['', Validators.required],
            lastname: ['', Validators.required],
            registrationNumber: ['', Validators.required],
            specialty: [null, Validators.required],
            gender: [null, Validators.required],
            phone: [''],
            email: ['', Validators.email]
        });
    }

    private loadTypes(): void {
        this.newOrderService.gettype('DOCTOR_SPECIALITY').subscribe({
            next: (result) => {
                if (Array.isArray(result.data)) {
                    this.specialty = result.data.map((item: any) => ({
                        id: item.idType,
                        label: item.description,
                        code: item.code
                    }));
                    const defaultSpecialty = this.specialty.find((s) => s.code === 'CLINICO');
                    if (defaultSpecialty) {
                        this.doctorForm.get('specialty')?.setValue(defaultSpecialty.id);
                    }
                }
            },
            error: () => {}
        });

        this.newOrderService.gettype('GENDER').subscribe({
            next: (result) => {
                if (Array.isArray(result.data)) {
                    this.gender = result.data.map((item: any) => ({
                        id: item.idType,
                        label: item.description,
                        code: item.code
                    }));
                }
            },
            error: () => {}
        });
    }

    getOrderStatusCodeById(id: number): string {
        return this.statusOrderMap[id] || '';
    }

    getStatusCodeById(id: number): string {
        return this.statusStudyMap[id] || '';
    }

    loadOrderDetail(): void {
        this.loading = true;

        this.orderDetailService.getOrderDetail(this.idOrder).subscribe({
            next: (response) => {
                this.orderDetail = response.data;
                this.studies = response.data.orderDetail || [];
                this.loading = false;
            },
            error: (error) => {
                console.error('Error al cargar detalle de orden:', error);
                this.showError('Error al cargar el detalle de la orden');
                this.loading = false;
            }
        });
    }

    getStatusSeverity(statusId: number): 'success' | 'info' | 'warn' | 'danger' {
        switch (statusId) {
            case 1:
                return 'danger';
            case 2:
                return 'warn';
            case 3:
                return 'info';
            case 4:
                return 'success';
            default:
                return 'info';
        }
    }

    getStatusLabel(statusId: number): string {
        switch (statusId) {
            case 1:
                return 'Pendiente';
            case 2:
                return 'En Proceso';
            case 3:
                return 'Incompleto';
            case 4:
                return 'Completado';
            default:
                return 'Desconocido';
        }
    }

    formatCurrency(amount: number): string {
        return new Intl.NumberFormat('es-PY', {
            style: 'currency',
            currency: 'PYG',
            minimumFractionDigits: 0
        }).format(amount);
    }

    haveRole(role: string): boolean {
        return this.rolUser.includes(role);
    }

    closeDialog(): void {
        this.ref.close();
    }

    // ============================================
    // #region Cambio de Doctor
    // ============================================

    openChangeDoctorModal(): void {
        this.doctorSearchForm.reset();
        this.searchPerformedDoctor = false;
        this.doctorsResults = [];
        this.selectedNewDoctor = null;
        this.showChangeDoctorModal = true;
    }

    closeChangeDoctorModal(): void {
        this.showChangeDoctorModal = false;
        this.doctorSearchForm.reset();
        this.searchPerformedDoctor = false;
        this.doctorsResults = [];
        this.selectedNewDoctor = null;
    }

    async searchnameDoctor(): Promise<void> {
        if (this.doctorSearchForm.invalid) {
            this.messageService.add({ severity: 'warn', summary: 'Advertencia', detail: 'Ingrese al menos 3 caracteres' });
            return;
        }
        const namedoc = this.doctorSearchForm.value.namedoctor;
        this.searchingDoctor = true;
        this.searchPerformedDoctor = false;
        this.doctorsResults = [];
        this.selectedNewDoctor = null;

        try {
            const response: any = await lastValueFrom(this.newOrderService.searchDoctorsByQuery(namedoc));
            let allDoctors: any[] = [];
            if (response?.data) {
                allDoctors = Array.isArray(response.data) ? response.data : [response.data];
            } else if (Array.isArray(response)) {
                allDoctors = response;
            }
            this.doctorsResults = allDoctors.map((d: any) => ({
                idDoctor: d.idDoctor,
                name: d.name,
                lastname: d.lastname,
                registrationNumber: d.registrationNumber,
                specialty: d.specialty || '',
                phone: d.phone || '',
                email: d.email || ''
            }));
            if (this.doctorsResults.length > 0) {
                this.messageService.add({ severity: 'success', summary: 'Éxito', detail: `${this.doctorsResults.length} doctor(es) encontrado(s)` });
            } else {
                this.messageService.add({ severity: 'warn', summary: 'Advertencia', detail: 'No se encontraron doctores' });
            }
        } catch (err) {
            this.doctorsResults = [];
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error al buscar doctor' });
        } finally {
            this.searchingDoctor = false;
            this.searchPerformedDoctor = true;
        }
    }

    selectNewDoctor(doctor: any): void {
        this.selectedNewDoctor = doctor;
    }

    openDoctorCreateModal(): void {
        this.doctorForm.reset();
        this.createAsBaseBranchDoctor = false;
        const defaultSpecialty = this.specialty.find((s) => s.code === 'CLINICO');
        if (defaultSpecialty) this.doctorForm.get('specialty')?.setValue(defaultSpecialty.id);
        const defaultGender = this.gender.find((g) => g.code === 'MALE');
        if (defaultGender) this.doctorForm.get('gender')?.setValue(defaultGender.id);
        this.showDoctorCreateModal = true;
    }

    saveDoctorAndSelect(): void {
        if (this.doctorForm.invalid) {
            this.messageService.add({ severity: 'warn', summary: 'Advertencia', detail: 'Complete los campos requeridos' });
            return;
        }
        this.savingDoctor = true;
        const doctorData = this.doctorForm.getRawValue();

        const doctorPayload = {
            name: doctorData.name,
            lastname: doctorData.lastname,
            ci: '',
            email: doctorData.email || '',
            phone: doctorData.phone || '',
            address: '',
            username: doctorData.registrationNumber,
            password: doctorData.registrationNumber,
            mustChangePassword: false,
            dateOfBirth: '',
            registrationNumber: doctorData.registrationNumber,
            idGender: doctorData.gender,
            idSpecialty: doctorData.specialty,
            idStatus: 1
        };

        this.orderService.createDoctor(doctorPayload).subscribe({
            next: (res) => {
                this.savingDoctor = false;
                const newDoc = {
                    idDoctor: res?.data?.idDoctor,
                    name: doctorData.name,
                    lastname: doctorData.lastname,
                    registrationNumber: doctorData.registrationNumber,
                    email: doctorData.email || '',
                    phone: doctorData.phone || ''
                };
                this.selectedNewDoctor = newDoc;
                this.doctorsResults = [newDoc, ...this.doctorsResults];
                this.showDoctorCreateModal = false;
                this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Doctor creado correctamente' });
            },
            error: () => {
                this.savingDoctor = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error al crear el doctor' });
            }
        });
    }

    async confirmDoctorChange(): Promise<void> {
        if (!this.selectedNewDoctor || !this.idOrder) return;

        let doctorId = this.selectedNewDoctor.idDoctor;

        if (!doctorId) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'El doctor seleccionado no tiene ID válido' });
            return;
        }

        this.updatingDoctor = true;

        try {
            await lastValueFrom(this.orderService.updateOrderDoctor(this.idOrder, doctorId));

            if (this.orderDetail) {
                this.orderDetail.doctorNameOrder = `${this.selectedNewDoctor.name} ${this.selectedNewDoctor.lastname}`;
            }

            this.updatingDoctor = false;
            this.showChangeDoctorModal = false;
            this.cdr.detectChanges();

            this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Doctor actualizado correctamente' });

            this.doctorSearchForm.reset();
            this.searchPerformedDoctor = false;
            this.doctorsResults = [];
            this.selectedNewDoctor = null;
        } catch (error) {
            this.updatingDoctor = false;
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo actualizar el doctor' });
        }
    }

    // ============================================
    // #region Cambio de Seguro
    // ============================================

    openChangeInsuranceModal(): void {
        this.selectedNewSecure = null;
        this.selectedNewSubsidiary = null;
        this.subsidiaries = [];
        this.showChangeInsuranceModal = true;
        if (this.secures.length === 0) {
            this.loadSecures();
        }
    }

    closeChangeInsuranceModal(): void {
        this.showChangeInsuranceModal = false;
        this.selectedNewSecure = null;
        this.selectedNewSubsidiary = null;
        this.subsidiaries = [];
    }

    loadSecures(): void {
        if (this.secures.length > 0) return;
        this.loadingSecures = true;
        this.newOrderService.getAllSecures().subscribe({
            next: (response: any) => {
                let processedSecures: any[] = [];
                if (response?.object && Array.isArray(response.object)) {
                    processedSecures = response.object.map((secure: any) => ({
                        idSecure: secure.idSecure,
                        code: secure.secureType,
                        name: secure.name,
                        discountPercentage: secure.discountPercentage || 0,
                        secureType: secure.idSecureType
                    }));
                } else if (Array.isArray(response)) {
                    processedSecures = response.map((secure: any) => ({
                        idSecure: secure.idSecure,
                        code: secure.secureType,
                        name: secure.name,
                        discountPercentage: secure.discountPercentage || 0,
                        secureType: secure.idSecureType
                    }));
                } else if (response?.data && Array.isArray(response.data)) {
                    processedSecures = response.data.map((secure: any) => ({
                        idSecure: secure.idSecure,
                        code: secure.secureType,
                        name: secure.name,
                        discountPercentage: secure.discountPercentage || 0,
                        secureType: secure.idSecureType
                    }));
                }
                this.secures = processedSecures;
                this.loadingSecures = false;
            },
            error: () => {
                this.loadingSecures = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error al cargar seguros' });
            }
        });
    }

    loadSubsidiariesForInsurance(idSecure: number): void {
        this.loadingSubsidiaries = true;
        this.subsidiaries = [];
        this.selectedNewSubsidiary = null;
        this.newOrderService.getSecureSubsidiaries(idSecure).subscribe({
            next: (response: any) => {
                let list: any[] = [];
                if (response?.object && Array.isArray(response.object)) {
                    list = response.object;
                } else if (Array.isArray(response)) {
                    list = response;
                } else if (response?.data && Array.isArray(response.data)) {
                    list = response.data;
                }
                this.subsidiaries = list.map((sub: any) => ({
                    idSecureSubsidiary: sub.idSecureSubsidiary,
                    name: sub.name,
                    code: sub.code || ''
                }));
                this.loadingSubsidiaries = false;
            },
            error: () => {
                this.loadingSubsidiaries = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error al cargar sucursales' });
            }
        });
    }

    onSecureChange(secure: any): void {
        this.selectedNewSecure = secure;
        this.selectedNewSubsidiary = null;
        this.subsidiaries = [];
        if (secure) {
            this.loadSubsidiariesForInsurance(secure.idSecure);
        }
    }

    confirmInsuranceChange(): void {
        if (!this.selectedNewSecure || !this.idOrder) {
            this.messageService.add({ severity: 'warn', summary: 'Advertencia', detail: 'Debe seleccionar un seguro' });
            return;
        }

        this.updatingInsurance = true;
        const secureId = this.selectedNewSecure.idSecure;
        const subsidiaryId = this.selectedNewSubsidiary?.idSecureSubsidiary || null;

        this.orderService.updateOrderSecure(this.idOrder, secureId, subsidiaryId).subscribe({
            next: () => {
                if (this.orderDetail) {
                    this.orderDetail.secure = this.selectedNewSecure.name;
                    this.orderDetail.idSecure = secureId;
                    this.orderDetail.subsidiarySecure = this.selectedNewSubsidiary
                        ? this.selectedNewSubsidiary.name
                        : '';
                    this.orderDetail.idSecureSubsidiary = subsidiaryId;
                }
                this.updatingInsurance = false;
                this.showChangeInsuranceModal = false;
                this.cdr.detectChanges();
                this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Seguro actualizado correctamente' });
                this.selectedNewSecure = null;
                this.selectedNewSubsidiary = null;
                this.subsidiaries = [];
            },
            error: () => {
                this.updatingInsurance = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo actualizar el seguro' });
            }
        });
    }

    // ============================================
    // #endregion Cambio de Seguro
    // ============================================

    private showError(message: string): void {
        this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: message
        });
    }
}
